import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Mail, CheckCircle } from 'lucide-react';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { forgotPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await forgotPassword(email);
      setSubmitted(true);
      toast.success('Instructies verzonden!');
    } catch (error) {
      // Always show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full relative overflow-hidden">
      {/* Animated Gradient Background - Same as Login */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(135deg, 
                #ff9a56 0%, 
                #ff6b8a 25%, 
                #c44eff 50%, 
                #7b68ee 75%, 
                #00bfff 100%
              )
            `,
            backgroundSize: '400% 400%',
            animation: 'gradientShift 15s ease infinite'
          }}
        />
        {/* Overlay waves */}
        <svg 
          className="absolute bottom-0 right-0 w-full h-full opacity-30"
          viewBox="0 0 1440 900" 
          preserveAspectRatio="xMaxYMax slice"
        >
          <path 
            fill="url(#wave1)" 
            d="M0,900 C300,700 600,800 900,600 C1200,400 1440,500 1440,0 L1440,900 Z"
          />
          <path 
            fill="url(#wave2)" 
            d="M0,900 C200,750 500,850 800,650 C1100,450 1440,550 1440,100 L1440,900 Z"
            opacity="0.7"
          />
          <defs>
            <linearGradient id="wave1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ff6b8a" />
              <stop offset="100%" stopColor="#c44eff" />
            </linearGradient>
            <linearGradient id="wave2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ffd194" />
              <stop offset="100%" stopColor="#ff6b8a" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Header */}
      <header className="relative z-10 p-6">
        <Link to="/" className="text-2xl font-bold tracking-tight text-black hover:opacity-80 transition-opacity">
          Spooker
        </Link>
      </header>

      {/* Card */}
      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-120px)] px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
            <div className="p-8">
              {!submitted ? (
                <>
                  {/* Back Link */}
                  <Link
                    to="/login"
                    className="inline-flex items-center text-sm text-neutral-500 hover:text-neutral-700 transition-colors mb-6"
                    data-testid="back-to-login-link"
                  >
                    <ArrowLeft className="mr-1 h-4 w-4" strokeWidth={1.5} />
                    Terug naar inloggen
                  </Link>

                  <h1 className="text-2xl font-semibold text-neutral-900 mb-2">
                    Wachtwoord vergeten
                  </h1>
                  <p className="text-sm text-neutral-600 mb-6">
                    Vul uw e-mailadres in en we sturen u instructies om uw wachtwoord te resetten.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-neutral-700">
                        E-mailadres
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="naam@bedrijf.nl"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="h-12 border-2 border-neutral-200 focus:border-[#635bff] focus:ring-[#635bff] rounded-lg"
                        data-testid="forgot-password-email-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-12 bg-[#635bff] hover:bg-[#4b45c6] text-white font-medium rounded-lg transition-colors"
                      disabled={loading}
                      data-testid="forgot-password-submit-button"
                    >
                      {loading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        'Verstuur reset-link'
                      )}
                    </Button>
                  </form>
                </>
              ) : (
                /* Success State */
                <div className="text-center py-4">
                  <div className="mx-auto h-16 w-16 rounded-full bg-green-50 flex items-center justify-center mb-6">
                    <CheckCircle className="h-8 w-8 text-green-600" strokeWidth={1.5} />
                  </div>
                  <h1 className="text-2xl font-semibold text-neutral-900 mb-2">
                    Controleer uw inbox
                  </h1>
                  <p className="text-sm text-neutral-600 mb-6">
                    Als dit e-mailadres bij ons bekend is, ontvangt u een reset-link.
                  </p>
                  <Link to="/login">
                    <Button
                      className="w-full h-12 bg-[#635bff] hover:bg-[#4b45c6] text-white font-medium rounded-lg transition-colors"
                      data-testid="back-to-login-button"
                    >
                      Terug naar inloggen
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 p-6 flex items-center justify-between text-sm text-neutral-600">
        <span>© 2026 Spooker</span>
        <div className="flex gap-4">
          <Link to="/privacy" className="hover:text-neutral-900">Privacy</Link>
          <Link to="/terms" className="hover:text-neutral-900">Voorwaarden</Link>
        </div>
      </footer>

      {/* CSS Animation */}
      <style>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
};

export default ForgotPasswordPage;
